// The pipeline state machine — client-side replacement for the desktop job runner.
//
//  Analysis job (/api/jobs):  download → transcribe → analyze
//      download : yt-dlp bridge → blob → cache at /clips/<id>/download/video.mp4
//      transcribe: bridge captions OR browser Whisper → words.json
//      analyze  : Gemini → clips.json  (artifact clips_metadata_path)
//
//  Render job (/api/video/process → poll /api/job/<id>):
//      per clip: renderClip(...) → cache at clips/<id>/clip_<n>.mp4 → completedClips[]
//
// All heavy compute runs here in the page; the SW only serves the cached bytes.

import { downloadVideo } from './bridge.js';
import { putBlob, putJson, getBlob, getJson } from './mediaStore.js';
import { transcribeVideo } from '../lib/engine/transcribe.js';
import { analyzeForClips } from '../lib/engine/analyze.js';
import { analyzeViaLlm } from '../lite/analyzeProxy.js';
import { assetService } from './assetService.js';
import { renderClip } from '../lib/engine/render.js';
import { isDemo, cannedAnalysis } from './demo.js';

const analysisJobs = new Map(); // id -> manifest
const renderJobs = new Map();   // id -> { completedClips, totalClips, status }

const uid = () => (crypto?.randomUUID?.() || `${Date.now()}-${Math.floor(Math.random() * 1e6)}`).slice(0, 18);

// ── crude VTT → word timings (when the bridge returns captions) ────────────────────
function parseVtt(vtt) {
  const words = [];
  const cueRe = /(\d{2}):(\d{2}):(\d{2})[.,](\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})[.,](\d{3})/;
  const lines = vtt.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const m = cueRe.exec(lines[i]);
    if (!m) continue;
    const start = +m[1] * 3600 + +m[2] * 60 + +m[3] + +m[4] / 1000;
    const end = +m[5] * 3600 + +m[6] * 60 + +m[7] + +m[8] / 1000;
    const text = (lines[i + 1] || '').replace(/<[^>]+>/g, '').trim();
    if (!text) continue;
    const toks = text.split(/\s+/);
    const per = (end - start) / Math.max(1, toks.length);
    toks.forEach((w, k) => words.push({ word: w, start: start + k * per, end: start + (k + 1) * per }));
  }
  return words;
}

// ── create / introspect ─────────────────────────────────────────────────────────
export function createAnalysisJob(body) {
  const id = uid();
  const phaseNames = body.phaseNames || ['download', 'transcribe', 'analyze'];
  analysisJobs.set(id, {
    id, status: 'queued', config: body.config || {},
    phases: phaseNames.map((name) => ({ name, status: 'queued', progress: 0 })),
    artifacts: {},
  });
  return id;
}

export function getAnalysisJob(id) { return analysisJobs.get(id); }
export function getRenderJob(id) { return renderJobs.get(id); }

function setPhase(job, name, patch) {
  const p = job.phases.find((x) => x.name === name);
  if (p) Object.assign(p, patch);
}

// ── analysis runner (fire-and-forget; UI polls getAnalysisJob) ─────────────────────
export async function runAnalysisJob(id) {
  const job = analysisJobs.get(id);
  if (!job || job.status === 'running' || job.status === 'completed') return;
  job.status = 'running';
  const cfg = job.config;
  const folder = `/clips/${id}`;

  try {
    // 1) download
    setPhase(job, 'download', { status: 'running', progress: 0 });
    const dl = await downloadVideo(
      { url: cfg.url, format_id: cfg.format_id, preferredLang: cfg.preferredLang, cookieBrowser: cfg.cookieBrowser },
      (p) => setPhase(job, 'download', { progress: p }),
    );
    const videoPath = `${folder}/download/video.mp4`;
    await putBlob(videoPath, dl.blob, 'video/mp4');
    job.artifacts.downloaded_video_path = videoPath;
    job.videoTitle = dl.title;
    setPhase(job, 'download', { status: 'completed', progress: 100 });

    // 2) transcribe (captions > Whisper > soft-skip)
    setPhase(job, 'transcribe', { status: 'running' });
    let words = [];
    let segments = [];
    if (isDemo()) {
      // offline demo: skip Whisper + Gemini entirely, use canned analysis
      setPhase(job, 'transcribe', { status: 'skipped', note: 'demo mode' });
      await putJson(`${folder}/transcribe/words.json`, words);
      setPhase(job, 'analyze', { status: 'running' });
      const analysis = cannedAnalysis(dl.duration, dl.title);
      const clipsPath = `${folder}/analyze/clips.json`;
      await putJson(clipsPath, analysis);
      job.artifacts.clips_metadata_path = clipsPath;
      setPhase(job, 'analyze', { status: 'completed' });
      job.status = 'completed';
      return;
    }
    if (dl.vtt) {
      words = parseVtt(dl.vtt);
      segments = words.length ? [{ start: words[0].start, end: words[words.length - 1].end, text: words.map((w) => w.word).join(' ') }] : [];
      setPhase(job, 'transcribe', { status: 'completed' });
    } else {
      try {
        const tr = await transcribeVideo(dl.blob, { language: cfg.preferredLang === 'en' ? 'en' : 'id' });
        words = tr.words; segments = tr.segments;
        setPhase(job, 'transcribe', { status: 'completed' });
      } catch (e) {
        if (cfg.allow_transcribe_skip) {
          setPhase(job, 'transcribe', { status: 'skipped', note: String(e.message || e) });
        } else { throw e; }
      }
    }
    await putJson(`${folder}/transcribe/words.json`, words);

    // 3) analyze
    setPhase(job, 'analyze', { status: 'running' });
    // Router / OpenAI-compatible provider picked in Settings ("Router / API lain"
    // tile): same analyzeViaLlm + chat.js path Lite uses, so OmniRoute on the
    // user's machine is called browser-direct. Otherwise the original Gemini path.
    const analysis = cfg.llm ? {
      ...(await analyzeViaLlm({
        llm: cfg.llm,
        segments,
        language: cfg.preferredLang === 'en' ? 'en' : 'id',
        maxDuration: cfg.advanced?.maxDuration,
        numCuts: cfg.advanced?.numCuts,
        instruction: cfg.instruction,
      })),
      source_info: { video_title: dl.title || 'Qlipper Web' },
    } : await analyzeForClips({
      segments,
      customPrompt: cfg.instruction,
      numCuts: cfg.advanced?.numCuts,
      maxDuration: cfg.advanced?.maxDuration,
      language: cfg.preferredLang === 'en' ? 'en' : 'id',
      videoTitle: dl.title,
      apiKey: cfg.apiKey,
      model: cfg.modelVariant, // the user's picked model (Settings → Model Variant)
    });
    const clipsPath = `${folder}/analyze/clips.json`;
    await putJson(clipsPath, analysis);
    job.artifacts.clips_metadata_path = clipsPath;
    setPhase(job, 'analyze', { status: 'completed' });

    job.status = 'completed';
  } catch (err) {
    const failed = job.phases.find((p) => p.status === 'running') || job.phases[0];
    if (failed) failed.status = 'failed';
    if (failed) failed.last_error = {
      code: err.code || 'PIPELINE_ERROR',
      ui_message: err.message || 'Pipeline failed',
      category: 'engine', retryable: true, remediations: [],
    };
    job.status = 'failed';
    console.error('[shim] analysis job failed:', err);
  }
}

// ── render runner (/api/video/process) ─────────────────────────────────────────────
export function createRenderJob(body) {
  const id = uid();
  const clips = body.config?.video_clips || [];
  renderJobs.set(id, { id, completedClips: [], totalClips: clips.length, status: 'processing' });
  runRenderJob(id, body); // fire-and-forget
  return { jobId: id, totalClips: clips.length };
}

async function runRenderJob(id, body) {
  const job = renderJobs.get(id);
  const { videoPath, config, outputFormat, outputResolution, styleSettings } = body;
  try {
    const sourceBlob = await getBlob(videoPath);
    if (!sourceBlob) throw new Error(`Source video not in store: ${videoPath}`);

    // word timings live alongside the source: /clips/<analysisId>/transcribe/words.json
    const analysisId = (videoPath.match(/\/clips\/([^/]+)\//) || [])[1];
    // …or, for a Local File upload (videoPath = uploads/<id>-<name>), in the asset service.
    const words = (analysisId && await getJson(`/clips/${analysisId}/transcribe/words.json`))
      || assetService.wordsForPath(videoPath) || [];

    const clips = config.video_clips || [];
    for (let i = 0; i < clips.length; i++) {
      const c = clips[i];
      try {
        const out = await renderClip({
          sourceBlob, start: c.start, end: c.end,
          outputFormat, outputResolution, styleSettings: styleSettings || {},
          hookText: c.hook || c.topic || '', wordTimings: words,
        });
        const url = `clips/${id}/clip_${i}.mp4`;
        await putBlob('/' + url, out, 'video/mp4');
        job.completedClips.push({
          id: c.clip_id, url, topic: c.topic || `Clip ${c.clip_id}`,
          status: 'completed', highlight: c.reason || '', reprocessedAt: Date.now(),
        });
      } catch (e) {
        // one bad clip must not kill the whole batch
        console.error(`[shim] clip ${i} render failed:`, e);
        job.completedClips.push({
          id: c.clip_id, url: '', topic: c.topic || `Clip ${c.clip_id}`,
          status: 'failed', highlight: e.message || 'render failed',
        });
      }
    }
    job.status = 'completed';
  } catch (err) {
    job.status = 'failed';
    job.error = err.message;
    console.error('[shim] render job failed:', err);
  }
}
